import {
  AccessReport,
  AccessReportsWizardAccessCriteria,
  AccessReportsWizardAnalysisTypeEnum,
  AccessReportsWizardCalculationMethodEnum,
  AccessReportsWizardElement,
  AccessReportsWizardFormGroup,
  AccessReportsWizardGroupPropertyIdEnum,
  AccessReportsWizardNetworkStandard,
  AccessReportsWizardNetworkStandardTypeEnum,
  AccessReportsWizardPage,
  AccessReportsWizardStepEnum,
  AccessReportsWizardTemplate,
} from "@app/access-reports/shared";
import { AccessReportParametersModel } from "@app/access-reports/wizard";
import { AuthenticationService } from "@app/authentication";
import { cloneDeep, flatten } from "lodash";

/* Interface taken from https://github.com/microsoft/TypeScript/issues/45612
 * msSaveOrOpenBlob was a proprietary Internet Explorer function, and Microsoft
 * removed it from their typescript definitions when it was removed from the W3C JS spec.
 * This declaration merges with their existing Navigator type definition.
 */
interface Navigator {
  msSaveOrOpenBlob: (blobOrBase64: Blob | string, filename: string) => void;
}

function buildStandard(accessCriteria: AccessReportsWizardAccessCriteria, accessReportParameters: AccessReportParametersModel): string {
  let standardString = "";

  switch (accessReportParameters.standard.id) {
    case AccessReportsWizardAnalysisTypeEnum.Standard:
      const standard: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.Standard.toUpperCase());

      if (standard) {
        switch (accessReportParameters.calculationMethod.id) {
          case AccessReportsWizardCalculationMethodEnum.Distance:
            standardString = "Standard: " + standard.minCount + " in " + standard.miles + "mi";
            break;
          case AccessReportsWizardCalculationMethodEnum.Time:
            standardString = "Standard: " + standard.minCount + " in " + standard.minutes + "min";
            break;
          case AccessReportsWizardCalculationMethodEnum.TimeAndDistance:
            standardString = "Standard: " + standard.minCount + " in " + standard.miles + "mi and " + standard.minutes + "min";
            break;
          case AccessReportsWizardCalculationMethodEnum.TimeOrDistance:
            standardString = "Standard: " + standard.minCount + " in " + standard.miles + "mi or " + standard.minutes + "min";
            break;
          default:
            standardString = "Error";
            break;
        }
      }

      break;
    case AccessReportsWizardAnalysisTypeEnum.DynamicByZipClass:
      const zipUrban: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.ZipUrban.toUpperCase());
      const zipSuburban: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.ZipSuburban.toUpperCase());
      const zipRural: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.ZipRural.toUpperCase());

      if (zipUrban && zipSuburban && zipRural) {
        switch (accessReportParameters.calculationMethod.id) {
          case AccessReportsWizardCalculationMethodEnum.Distance:
            standardString = (
              "U: " + zipUrban.minCount + " in " + zipUrban.miles + "mi" + "\n" +
              "S: " + zipSuburban.minCount + " in " + zipSuburban.miles + "mi" + "\n" +
              "R: " + zipRural.minCount + " in " + zipRural.miles + "mi"
            );
            break;
          case AccessReportsWizardCalculationMethodEnum.Time:
            standardString = (
              "U: " + zipUrban.minCount + " in " + zipUrban.minutes + "min" + "\n" +
              "S: " + zipSuburban.minCount + " in " + zipSuburban.minutes + "min" + "\n" +
              "R: " + zipRural.minCount + " in " + zipRural.minutes + "min"
            );
            break;
          case AccessReportsWizardCalculationMethodEnum.TimeAndDistance:
            standardString = (
              "U: " + zipUrban.minCount + " in " + zipUrban.miles + "mi and " + zipUrban.minutes + "min" + "\n" +
              "S: " + zipSuburban.minCount + " in " + zipSuburban.miles + "mi and " + zipSuburban.minutes + "min" + "\n" +
              "R: " + zipRural.minCount + " in " + zipRural.miles + "mi and " + zipRural.minutes + "min"
            );
            break;
          case AccessReportsWizardCalculationMethodEnum.TimeOrDistance:
            standardString = (
              "U: " + zipUrban.minCount + " in " + zipUrban.miles + "mi or " + zipUrban.minutes + "min" + "\n" +
              "S: " + zipSuburban.minCount + " in " + zipSuburban.miles + "mi or " + zipSuburban.minutes + "min" + "\n" +
              "R: " + zipRural.minCount + " in " + zipRural.miles + "mi or " + zipRural.minutes + "min"
            );
            break;
          default:
            standardString = "Error";
            break;
        }
      }

      break;
    case AccessReportsWizardAnalysisTypeEnum.DynamicByCountyClass:
      const countyLarge: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.CountyLarge.toUpperCase());
      const countyMetro: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.CountyMetro.toUpperCase());
      const countyMicro: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.CountyMicro.toUpperCase());
      const countyRural: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.CountyRural.toUpperCase());
      const countyCEAC: AccessReportsWizardNetworkStandard = accessCriteria.standards.find((x) => x.type.toUpperCase() === AccessReportsWizardNetworkStandardTypeEnum.CountyCEAC.toUpperCase());

      if (countyLarge && countyMetro && countyMicro && countyRural && countyCEAC) {
        switch (accessReportParameters.calculationMethod.id) {
          case AccessReportsWizardCalculationMethodEnum.Distance:
            standardString = (
              "L: " + countyLarge.minCount + " in " + countyLarge.miles + "mi" + "\n" +
              "Me: " + countyMetro.minCount + " in " + countyMetro.miles + "mi" + "\n" +
              "Mi: " + countyMicro.minCount + " in " + countyMicro.miles + "mi" + "\n" +
              "R: " + countyRural.minCount + " in " + countyRural.miles + "mi" + "\n" +
              "C: " + countyCEAC.minCount + " in " + countyCEAC.miles + "mi."
            );
            break;
          case AccessReportsWizardCalculationMethodEnum.Time:
            standardString = (
              "L: " + countyLarge.minCount + " in " + countyLarge.minutes + "min" + "\n" +
              "Me: " + countyMetro.minCount + " in " + countyMetro.minutes + "min" + "\n" +
              "Mi: " + countyMicro.minCount + " in " + countyMicro.minutes + "min" + "\n" +
              "R: " + countyRural.minCount + " in " + countyRural.minutes + "min" + "\n" +
              "C: " + countyCEAC.minCount + " in " + countyCEAC.minutes + "min."
            );
            break;
          case AccessReportsWizardCalculationMethodEnum.TimeAndDistance:
            standardString = (
              "L: " + countyLarge.minCount + " in " + countyLarge.miles + "mi and " + countyLarge.minutes + "min" + "\n" +
              "Me: " + countyMetro.minCount + " in " + countyMetro.miles + "mi and " + countyMetro.minutes + "min" + "\n" +
              "Mi: " + countyMicro.minCount + " in " + countyMicro.miles + "mi and " + countyMicro.minutes + "min" + "\n" +
              "R: " + countyRural.minCount + " in " + countyRural.miles + "mi and " + countyRural.minutes + "min" + "\n" +
              "C: " + countyCEAC.minCount + " in " + countyCEAC.miles + "mi and " + countyCEAC.minutes + "min."
            );
            break;
          case AccessReportsWizardCalculationMethodEnum.TimeOrDistance:
            standardString = (
              "L: " + countyLarge.minCount + " in " + countyLarge.miles + "mi or " + countyLarge.minutes + "min" + "\n" +
              "Me: " + countyMetro.minCount + " in " + countyMetro.miles + "mi or " + countyMetro.minutes + "min" + "\n" +
              "Mi: " + countyMicro.minCount + " in " + countyMicro.miles + "mi or " + countyMicro.minutes + "min" + "\n" +
              "R: " + countyRural.minCount + " in " + countyRural.miles + "mi or " + countyRural.minutes + "min" + "\n" +
              "C: " + countyCEAC.minCount + " in " + countyCEAC.miles + "mi or " + countyCEAC.minutes + "min."
            );
            break;
          default:
            standardString = "Error";
            break;
        }
      }
      break;
    default:
      standardString = "Error";
  }

  return standardString;
}

// CLS-AR: NEED TO REVISIT THIS AND FIGURE OUT WHY USING THE accessReportService wouldn't work and what makes this work (hint: think it's arraybuffer)
async function downloadFile(url: string, download: boolean, filename: string, contentType: string) {
  const loaded = $.Deferred();
  const request = new XMLHttpRequest();
  request.open("GET", url);

  const user = await AuthenticationService.getInstance().getUser();

  request.setRequestHeader("Authorization", `Bearer ${user ? user.access_token : null}`);
  request.setRequestHeader("Cache-Control", "no-store");
  request.setRequestHeader("Pragma", "no-cache");

  // ! setting the responseType has to be after the `request.open`
  // `arraybuffer` is necessary for Internet Explorer and Edge becuase
  // they do not support `blob` as response type
  request.responseType = "arraybuffer";
  request.onload = () => {
    loaded.resolve();
    const file = new Blob([request.response], { type: contentType });
    // For Internet Explorer and Edge
    if ("msSaveOrOpenBlob" in window.navigator) {
      (window.navigator as Navigator).msSaveOrOpenBlob(file, filename);
    } else {
      // Bind blob on disk to ObjectURL
      const data = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.setAttribute("style", "display: none;");
      a.href = data;
      if (!download) {
        a.target = "_blank";
      }
      if (download) {
        if (contentType == "application/quest")
          a.download = filename + ".qpx";
        else
          a.download = filename;
      }
      document.body.appendChild(a);
      a.click();
      // For Firefox
      setTimeout(() => {
        document.body.removeChild(a);
        // Release resource on disk after triggering the download
        window.URL.revokeObjectURL(data);
      }, 100);
    }
  };
  request.onloadend = () => { loaded.resolve(); };
  request.send();

  return loaded;
}

async function handleFileLinkClick(accessReport: AccessReport, download: boolean, fileId: string): Promise<void> {
  // CLS-AR: download
  const url = `${window.apiEndpointV2_0}/accessreports/${accessReport.accessReportId}/files/${fileId}`;
  let contentType = "";
  switch (fileId) {
    case "PDF":
      contentType = "application/pdf";
      break;
    case "QPX":
      contentType = "application/quest";
      break;
    default:
      contentType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      break;
  }

  downloadFile(url, download, !!accessReport.reportTitle ? accessReport.reportTitle : `Access Report ${accessReport.accessReportId}`, contentType);
}

function getPageFromTemplate(template: AccessReportsWizardTemplate, page: AccessReportsWizardStepEnum): AccessReportsWizardPage {
  return template.pages.find(x => x.title === page);
}

function getFormGroupByGroupPropertyIdFromTemplate(template: AccessReportsWizardTemplate, page: AccessReportsWizardStepEnum, groupPropertyId?: AccessReportsWizardGroupPropertyIdEnum): AccessReportsWizardFormGroup {
  const templatePage: AccessReportsWizardPage = getPageFromTemplate(template, page);

  if (!!groupPropertyId) {
    return templatePage?.formGroups.find(x => x.groupPropertyId === groupPropertyId);
  } else {
    return templatePage?.formGroups.find(x => !x.groupPropertyId);
  }
}

function getFormGroupByIdFromTemplate(template: AccessReportsWizardTemplate, page: AccessReportsWizardStepEnum, id: number): AccessReportsWizardFormGroup {
  const templatePage: AccessReportsWizardPage = getPageFromTemplate(template, page);
  return templatePage?.formGroups.find(x => x.id === id);
}

function getAllElementsByPageFromTemplate(template: AccessReportsWizardTemplate, page: AccessReportsWizardStepEnum): AccessReportsWizardElement[] {
  const templatePage: AccessReportsWizardPage = getPageFromTemplate(template, page);
  return flatten(templatePage?.formGroups.map(x => x.elements));
}

function getElementByIdFromTemplate(template: AccessReportsWizardTemplate, page: AccessReportsWizardStepEnum, elementId: string): AccessReportsWizardElement {
  const allElements: AccessReportsWizardElement[] = getAllElementsByPageFromTemplate(template, page);
  return allElements.find(x => x.elementId === elementId)

}

function assignTemplateMultiElementOptions(template: AccessReportsWizardTemplate): AccessReportsWizardTemplate {
  const multiElementOptionsPage: AccessReportsWizardPage = getPageFromTemplate(template, AccessReportsWizardStepEnum.MultiElementOptions);
  const multiElementOptionsElements: AccessReportsWizardElement[] = flatten(multiElementOptionsPage?.formGroups.map(x => x.elements));
  const pagesWithoutOptions: AccessReportsWizardStepEnum[] = [
    AccessReportsWizardStepEnum.MultiElementOptions,
    AccessReportsWizardStepEnum.Population,
    AccessReportsWizardStepEnum.Review,
  ];

  for (const multiElementOptionElement of multiElementOptionsElements) {
    for (const page of template.pages) {
      if (!pagesWithoutOptions.includes(page.title)) {
        const pageElements: AccessReportsWizardElement[] = flatten(page.formGroups.map(x => x.elements));

        for (const element of pageElements) {
          if (element.multiElementOptionEnum === multiElementOptionElement.elementId) {
            element.options = cloneDeep(multiElementOptionElement.options);
          }
        }
      }
    }
  }

  return template;
}

export {
  assignTemplateMultiElementOptions,
  buildStandard,
  handleFileLinkClick,
  getAllElementsByPageFromTemplate,
  getElementByIdFromTemplate,
  getFormGroupByGroupPropertyIdFromTemplate,
  getFormGroupByIdFromTemplate,
  getPageFromTemplate,
};